/**
 * Example Constellation Presets
 * Pre-configured constellations for quick onboarding
 */

import type { SavedConstellation } from '../types/explore.types';

export const EXAMPLE_CONSTELLATIONS: SavedConstellation[] = [
  {
    id: 'cosmic-jazz',
    name: 'Cosmic Jazz',
    description: 'Otherworldly, spacious jazz with cold textures',
    nodes: [
      { type: 'artist', value: 'Sun Ra', weight: 0.8 },
      { type: 'genre', value: 'Jazz', weight: 0.7 },
      { type: 'mood', value: 'Spacious', weight: 0.6 },
      { type: 'texture', value: 'Cold', weight: 0.7 },
    ],
    vibe: {
      groove: 0.3,
      temperature: 0.2,
      obscurity: 0.7,
      density: 0.4,
    },
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 'dark-techno',
    name: 'Dark Techno',
    description: 'Warehouse techno with cold, dense production',
    nodes: [
      { type: 'scene', value: 'Berlin Warehouse', weight: 0.8 },
      { type: 'genre', value: 'Techno', weight: 0.9 },
      { type: 'texture', value: 'Cold', weight: 0.8 },
      { type: 'characteristic', value: 'Hypnotic', weight: 0.6 },
    ],
    vibe: {
      groove: 0.7,
      temperature: 0.1,
      obscurity: 0.6,
      density: 0.8,
    },
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 'warm-ambient',
    name: 'Warm Ambient',
    description: 'Gentle, intimate ambient music with minimal production',
    nodes: [
      { type: 'artist', value: 'Brian Eno', weight: 0.7 },
      { type: 'genre', value: 'Ambient', weight: 0.9 },
      { type: 'mood', value: 'Peaceful', weight: 0.7 },
      { type: 'characteristic', value: 'Minimal', weight: 0.6 },
    ],
    vibe: {
      groove: 0.2,
      temperature: 0.8,
      obscurity: 0.4,
      density: 0.2,
    },
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 'psychedelic-funk',
    name: 'Psychedelic Funk',
    description: 'Groovy, warm funk with psychedelic elements',
    nodes: [
      { type: 'artist', value: 'Funkadelic', weight: 0.8 },
      { type: 'genre', value: 'Funk', weight: 0.8 },
      { type: 'mood', value: 'Psychedelic', weight: 0.7 },
      { type: 'era', value: '1970s', weight: 0.6 },
    ],
    vibe: {
      groove: 0.9,
      temperature: 0.7,
      obscurity: 0.5,
      density: 0.7,
    },
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 'ethereal-vocals',
    name: 'Ethereal Vocals',
    description: 'Cold, reverb-heavy vocals with atmospheric textures',
    nodes: [
      { type: 'artist', value: 'Cocteau Twins', weight: 0.8 },
      { type: 'mood', value: 'Ethereal', weight: 0.9 },
      { type: 'texture', value: 'Reverb', weight: 0.8 },
      { type: 'characteristic', value: 'Dreamlike', weight: 0.7 },
    ],
    vibe: {
      groove: 0.3,
      temperature: 0.3,
      obscurity: 0.6,
      density: 0.5,
    },
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 'japanese-city-pop',
    name: 'Japanese City Pop',
    description: 'Warm, groovy 80s Japanese pop with nostalgic vibes',
    nodes: [
      { type: 'scene', value: 'Tokyo', weight: 0.8 },
      { type: 'era', value: '1980s', weight: 0.9 },
      { type: 'genre', value: 'City Pop', weight: 0.8 },
      { type: 'mood', value: 'Nostalgic', weight: 0.7 },
    ],
    vibe: {
      groove: 0.7,
      temperature: 0.7,
      obscurity: 0.7,
      density: 0.6,
    },
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 'experimental-electronics',
    name: 'Experimental Electronics',
    description: 'Cold, dense experimental electronic music',
    nodes: [
      { type: 'artist', value: 'Autechre', weight: 0.8 },
      { type: 'genre', value: 'Experimental', weight: 0.9 },
      { type: 'characteristic', value: 'Abstract', weight: 0.8 },
      { type: 'texture', value: 'Glitchy', weight: 0.7 },
    ],
    vibe: {
      groove: 0.4,
      temperature: 0.2,
      obscurity: 0.8,
      density: 0.8,
    },
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 'folk-intimacy',
    name: 'Folk Intimacy',
    description: 'Warm, minimal folk with intimate vocals',
    nodes: [
      { type: 'genre', value: 'Folk', weight: 0.8 },
      { type: 'mood', value: 'Intimate', weight: 0.9 },
      { type: 'instrument', value: 'Acoustic Guitar', weight: 0.7 },
      { type: 'characteristic', value: 'Storytelling', weight: 0.6 },
    ],
    vibe: {
      groove: 0.3,
      temperature: 0.8,
      obscurity: 0.5,
      density: 0.2,
    },
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 'post-punk-revival',
    name: 'Post-Punk Revival',
    description: 'Cold, angular post-punk with driving rhythms',
    nodes: [
      { type: 'genre', value: 'Post-Punk', weight: 0.9 },
      { type: 'era', value: '2000s', weight: 0.6 },
      { type: 'texture', value: 'Angular', weight: 0.8 },
      { type: 'mood', value: 'Urgent', weight: 0.7 },
    ],
    vibe: {
      groove: 0.6,
      temperature: 0.3,
      obscurity: 0.6,
      density: 0.6,
    },
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 'afrobeat-fusion',
    name: 'Afrobeat Fusion',
    description: 'Warm, groovy Afrobeat with dense polyrhythms',
    nodes: [
      { type: 'genre', value: 'Afrobeat', weight: 0.9 },
      { type: 'artist', value: 'Fela Kuti', weight: 0.7 },
      { type: 'characteristic', value: 'Polyrhythmic', weight: 0.8 },
      { type: 'mood', value: 'Energetic', weight: 0.8 },
    ],
    vibe: {
      groove: 0.9,
      temperature: 0.7,
      obscurity: 0.5,
      density: 0.8,
    },
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
];
